import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Loader2, Download, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { CreateVisibility } from "@/components/client/CreateVisibility";
import { useUsers } from "@/lib/hooks";
import { openBlob } from "@/lib/invoices";

const STEPS = ["Carica file", "Abbina colonne", "Anteprima", "Importa"];
const CHUNK = 100;

const download = async (ext) => {
  try { const r = await api.get(`/clients/import/modello.${ext}`, { responseType: "blob" }); openBlob(r.data, `modello-clienti-core.${ext}`, true); }
  catch { toast.error("Download non riuscito"); }
};

const Upload1 = ({ onParsed }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) return toast.error("Carica un file .xlsx, .xls o .csv");
    if (f.size > 5 * 1024 * 1024) return toast.error("Il file supera i 5 MB");
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/clients/import/analizza", fd); onParsed({ ...r.data, nome: f.name }); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div onClick={() => ref.current.click()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-[#E8173A] bg-red-50/40" : "border-[#0A1128]/25 bg-[#A6DAFC] hover:border-[#E8173A]"}`} data-testid="import-dropzone">
        <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="import-file-input" />
        {busy ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0A1128]" /> : <Upload className="h-8 w-8 mx-auto text-[#0A1128]" />}
        <div className="font-bold text-[#0A1128] mt-2">Trascina qui il file Excel o CSV, oppure clicca</div>
        <div className="text-xs text-slate-600 mt-1">.xlsx, .xls o .csv (anche con «;» come separatore) · max 5 MB · max 2000 righe</div>
      </div>
      <div className="flex items-center gap-2 text-sm text-[#0A1128]">
        <span>Non hai un file pronto? Scarica il modello:</span>
        <Button size="sm" variant="outline" onClick={() => download("xlsx")} className="rounded-full font-bold" data-testid="import-template-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        <Button size="sm" variant="outline" onClick={() => download("csv")} className="rounded-full font-bold" data-testid="import-template-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
      </div>
    </div>
  );
};

const Mapping2 = ({ file, mapping, setMapping }) => {
  const used = Object.values(mapping);
  return (
    <div className="space-y-2" data-testid="import-mapping">
      <div className="text-sm text-slate-600 mb-2">{file.righe.length} righe in «{file.nome}». Abbiamo abbinato le colonne in automatico: controlla e correggi se serve.</div>
      {!used.includes("ragione_sociale") && <div className="text-sm font-bold text-[#C8102E]" data-testid="import-mapping-missing">Abbina una colonna a «Ragione sociale» (obbligatoria).</div>}
      <div className="max-h-[50vh] overflow-y-auto core-scroll space-y-1.5 pr-1">
        {file.headers.map((h) => (
          <div key={h} className="grid grid-cols-[1fr_1fr] gap-3 items-center rounded-xl bg-[#A6DAFC]/60 px-3 py-2">
            <div className="min-w-0"><div className="font-bold text-sm text-[#0A1128] truncate">{h}</div>
              <div className="text-xs text-slate-600 truncate">es. {file.righe.find((r) => r[h])?.[h] || "—"}</div></div>
            <Select value={mapping[h] || "_"} onValueChange={(v) => setMapping((m) => { const n = { ...m }; if (v === "_") delete n[h]; else { Object.keys(n).forEach((k) => n[k] === v && delete n[k]); n[h] = v; } return n; })}>
              <SelectTrigger className="rounded-xl bg-white" data-testid={`import-map-${h}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="_">— Non importare —</SelectItem>{file.campi.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
};

const Preview3 = ({ prev, setPrev }) => {
  const setAz = (i, azione) => setPrev((p) => ({ ...p, righe: p.righe.map((r, j) => (j === i ? { ...r, azione } : r)) }));
  return (
    <div data-testid="import-preview">
      <div className="flex gap-3 text-sm mb-3">
        <span className="font-bold text-emerald-700">{prev.totali.valide} valide</span>·<span className="font-bold text-[#C8102E]">{prev.totali.errori} con errori (saltate)</span>·<span className="font-bold text-amber-700">{prev.totali.duplicati} possibili duplicati</span>
      </div>
      <div className="max-h-[50vh] overflow-auto core-scroll rounded-xl border border-[#5FB3EA]">
        <table className="w-full text-sm">
          <thead className="bg-[#A6DAFC] sticky top-0 text-left text-xs font-bold uppercase tracking-wider text-[#0A1128]"><tr><th className="px-3 py-2">Riga</th><th className="px-3 py-2">Ragione sociale</th><th className="px-3 py-2">Email / P.IVA</th><th className="px-3 py-2">Controlli</th><th className="px-3 py-2">Azione</th></tr></thead>
          <tbody>{prev.righe.map((r, i) => (
            <tr key={r.riga} className="border-t border-[#5FB3EA]/60 align-top" data-testid={`import-row-${r.riga}`}>
              <td className="px-3 py-2 text-slate-500">{r.riga}</td>
              <td className="px-3 py-2 font-bold text-[#0A1128]">{r.dati.ragione_sociale || "—"}</td>
              <td className="px-3 py-2 text-xs">{r.dati.email}<div className="text-slate-500">{r.dati.piva}</div></td>
              <td className="px-3 py-2 text-xs space-y-0.5">
                {r.errori.map((e) => <div key={e} className="flex items-center gap-1 text-[#C8102E] font-bold"><XCircle className="h-3.5 w-3.5" />{e}</div>)}
                {r.esistente && <div className="flex items-center gap-1 text-amber-800 font-bold"><AlertTriangle className="h-3.5 w-3.5" />Già presente: {r.esistente.nome}{r.esistente.archiviato ? " (archiviato)" : ""}</div>}
                {r.avvisi.map((a) => <div key={a} className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />{a}</div>)}
                {!r.errori.length && !r.avvisi.length && !r.esistente && <div className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />OK</div>}
              </td>
              <td className="px-3 py-2">
                {r.errori.length ? <span className="text-xs font-bold text-slate-500">Salta</span> : (r.esistente || r.duplicato_file) ? (
                  <Select value={r.azione} onValueChange={(v) => setAz(i, v)}>
                    <SelectTrigger className="rounded-xl h-8 w-40 bg-white" data-testid={`import-action-${r.riga}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="salta">Salta</SelectItem>{r.esistente && <SelectItem value="aggiorna">Aggiorna esistente</SelectItem>}<SelectItem value="crea">Crea comunque</SelectItem></SelectContent>
                  </Select>) : <span className="text-xs font-bold text-emerald-700">Crea</span>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};

const reportCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = ["Riga;Ragione sociale;Esito;Dettaglio", ...rows.map((r) => [r.riga, r.ragione_sociale, r.esito, r.esito === "creato" ? "" : r.esito === "saltato" ? "Saltato su richiesta" : r.dettaglio].map(esc).join(";"))].join("\n");
  openBlob(new Blob(["\ufeff" + body], { type: "text/csv;charset=utf-8" }), "report-importazione-clienti.csv", true);
};

export const ClientImportDialog = ({ open, onOpenChange, onDone }) => {
  const [step, setStep] = useState(0);
  const [vis, setVis] = useState({ visibilita: "tutti", membri: [] });
  const { data: allUsers } = useUsers();
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState({});
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const reset = () => { setStep(0); setFile(null); setMapping({}); setPrev(null); setResult(null); setProgress(0); };
  const close = (o) => { if (busy) return; onOpenChange(o); if (!o) { if (result) onDone(); reset(); } };
  const toPreview = async () => {
    setBusy(true);
    try { setPrev((await api.post("/clients/import/anteprima", { mapping, righe: file.righe })).data); setStep(2); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const run = async () => {
    setStep(3); setBusy(true);
    const rows = prev.righe.map((r) => ({ riga: r.riga, dati: r.dati, azione: r.errori.length ? "salta" : r.azione, esistente_id: r.esistente?.id || null, errori: r.errori }));
    const out = [];
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        const chunk = rows.slice(i, i + CHUNK);
        const last = i + CHUNK >= rows.length;
        const r = await api.post("/clients/import/esegui", { righe: chunk, ultimo: false, ...vis });
        out.push(...r.data.risultati);
        if (last) {
          const t = { creati: out.filter((x) => x.esito === "creato").length, aggiornati: out.filter((x) => x.esito === "aggiornato").length,
            saltati: out.filter((x) => x.esito === "saltato").length, errori: out.filter((x) => x.esito === "errore").length };
          await api.post("/clients/import/esegui", { righe: [], ultimo: true, totali: t });
          setResult({ ...t, righe: out });
          window.dispatchEvent(new Event("core-clients-changed"));
        }
        setProgress(Math.round((Math.min(i + CHUNK, rows.length) / rows.length) * 100));
      }
    } catch (e) { toast.error(errMsg(e)); setResult({ creati: out.filter((x) => x.esito === "creato").length, aggiornati: out.filter((x) => x.esito === "aggiornato").length, saltati: 0, errori: rows.length - out.length, righe: out, interrotto: true }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl rounded-2xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#0A1128]">Importa clienti da Excel/CSV</DialogTitle>
          <DialogDescription asChild><div className="flex gap-2 pt-1">{STEPS.map((s, i) => (
            <span key={s} className={`text-xs font-bold rounded-full px-3 py-1 ${i === step ? "bg-[#0A1128] text-white" : i < step ? "bg-[#A6DAFC] text-[#0A1128]" : "bg-slate-100 text-slate-500"}`} data-testid={`import-step-${i}`}>{i + 1}. {s}</span>
          ))}</div></DialogDescription>
        </DialogHeader>
        {step === 0 && <Upload1 onParsed={(f) => { setFile(f); setMapping(f.mapping); setStep(1); }} />}
        {step === 1 && file && <Mapping2 file={file} mapping={mapping} setMapping={setMapping} />}
        {step === 2 && prev && <><Preview3 prev={prev} setPrev={setPrev} /><div className="mt-4"><CreateVisibility value={vis} onChange={setVis} users={allUsers || []} title="Visibilità dei clienti importati" testid="import-visibility" /></div></>}
        {step === 3 && (
          <div className="py-4 space-y-4" data-testid="import-progress">
            <Progress value={progress} className="h-3" />
            {result ? (
              <div data-testid="import-report">
                <div className="grid grid-cols-4 gap-3">
                  {[["creati", "Creati", "text-emerald-700"], ["aggiornati", "Aggiornati", "text-[#0A1128]"], ["saltati", "Saltati", "text-slate-600"], ["errori", "Errori", "text-[#C8102E]"]].map(([k, l, c]) => (
                    <div key={k} className="rounded-xl bg-[#A6DAFC]/60 p-4 text-center" data-testid={`import-report-${k}`}><div className={`font-display text-3xl font-black ${c}`}>{result[k]}</div><div className="text-xs font-bold uppercase tracking-wider text-slate-600">{l}</div></div>
                  ))}
                </div>
                {result.interrotto && <div className="text-sm text-[#C8102E] font-bold mt-3">Importazione interrotta: alcune righe non sono state elaborate.</div>}
              </div>
            ) : <div className="text-sm text-slate-600">Importazione in corso… {progress}%</div>}
          </div>
        )}
        <DialogFooter>
          {step === 1 && <><Button variant="ghost" className="rounded-full" onClick={() => setStep(0)}>Indietro</Button>
            <Button onClick={toPreview} disabled={busy || !Object.values(mapping).includes("ragione_sociale")} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="import-to-preview">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Continua</Button></>}
          {step === 2 && <><Button variant="ghost" className="rounded-full" onClick={() => setStep(1)}>Indietro</Button>
            <Button onClick={run} disabled={!prev.righe.some((r) => !r.errori.length && r.azione !== "salta")} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="import-run">Importa {prev.righe.filter((r) => !r.errori.length && r.azione !== "salta").length} righe</Button></>}
          {step === 3 && result && <><Button variant="outline" onClick={() => reportCsv(result.righe)} className="rounded-full font-bold" data-testid="import-report-csv"><Download className="h-4 w-4 mr-1.5" />Scarica report CSV</Button>
            <Button onClick={() => close(false)} className="rounded-full bg-[#0A1128] font-bold" data-testid="import-close">Chiudi</Button></>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
